import { computed, inject, Injectable, signal, Signal } from "@angular/core";
import { FactureStoreService } from "./facture-store.service";
import { PaginatedEntityFacadeService } from "../../../../shared/store/pagination/paginated-entity-facade.service";
import { FactureApiService } from "./facture-api.service";
import { Facture, FactureKpis, FactureLigne } from "../../models/facture.model";
import {
  Untrack,
  UpdateStatus,
} from "../../../../shared/store/utils/store-utils.service";
import { firstValueFrom } from "rxjs";
import { BonLivraison } from "../../../bon-livraison/models/bon-livraison.model";
import { FactureMailPayload } from "../../models/facture-mail-payload.model";

@Injectable({ providedIn: "root" })
export class FactureFacadeService extends PaginatedEntityFacadeService<
  Facture,
  FactureApiService
> {
  private factureStore = inject(FactureStoreService);
  selectedFactureId: Signal<string | null>;
  selectedFacture: Signal<Facture | null>;
  kpis = signal<FactureKpis>({
    enAttenteCount: 0,
    enAttenteTTC: 0,
    echeanceDepasseeCount: 0,
    echeanceDepasseeTTC: 0,
  });

  constructor() {
    const api = inject(FactureApiService);
    const store = inject(FactureStoreService);
    super(api, store as any);
    this.store.initStatus(FactureFacadeService);

    const state = this.factureStore.getWritableState();

    this.selectedFactureId = computed(() => state.selectedFactureId());

    this.selectedFacture = computed(() => {
      const id = state.selectedFactureId();
      if (!id) return null;
      const entity = this.getById(id);
      return entity ? entity() : null;
    });
  }

  selectFacture(id: string | null) {
    this.factureStore.patchState({ selectedFactureId: id, selectedBlId: null });
  }

  selectBl(id: string | null) {
    this.factureStore.patchState({ selectedBlId: id, selectedFactureId: null });
  }

  clearSelection() {
    this.factureStore.patchState({ selectedFactureId: null, selectedBlId: null });
  }

  upsertInStore(facture: Facture) {
    this.store.upsertOne(facture);
  }

  @Untrack
  @UpdateStatus
  async loadKpis() {
    const kpis = await firstValueFrom(this.api.loadKpis());
    this.kpis.set(kpis);
    return kpis;
  }

  @Untrack
  @UpdateStatus
  async saveLigne(ligne: FactureLigne) {
    const savedFacture = await firstValueFrom(this.api.saveLigne(ligne));
    this.store.upsertOne(savedFacture);
    return savedFacture;
  }

  @Untrack
  @UpdateStatus
  async deleteLigne(id: string) {
    const savedFacture = await firstValueFrom(this.api.deleteLigne(id));
    this.store.upsertOne(savedFacture);
    return savedFacture;
  }

  @Untrack
  @UpdateStatus
  async createFromBL(bl: BonLivraison) {
    const savedFacture = await firstValueFrom(this.api.createFromBL(bl));
    this.store.upsertOne(savedFacture);
    return savedFacture;
  }

  @Untrack
  @UpdateStatus
  async addBonLivraisonToFacture(bls: BonLivraison[], factureId: string) {
    const savedFacture = await firstValueFrom(
      this.api.addBonLivraisonToFacture(bls, factureId),
    );
    this.store.upsertOne(savedFacture);
    return savedFacture;
  }

  @Untrack
  @UpdateStatus
  async removeBonLivraisonFromFacture(id: string, factureId: string) {
    const savedFacture = await firstValueFrom(
      this.api.removeBonLivraisonFromFacture(id, factureId),
    );
    this.store.upsertOne(savedFacture);
    return savedFacture;
  }

  @Untrack
  @UpdateStatus
  async figerFacture(factureId: string) {
    const savedFacture = await firstValueFrom(this.api.onFiger(factureId));
    this.store.upsertOne(savedFacture);
    return savedFacture;
  }

  @Untrack
  @UpdateStatus
  async removeFactureFromAvoir(avoir: Facture, facture: Facture) {
    const savedFacture = await firstValueFrom(
      this.api.removeFactureFromAvoir(avoir, facture),
    );
    this.store.upsertOne(savedFacture);
    return savedFacture;
  }

  @Untrack
  @UpdateStatus
  async makeAvoirFromFacture(facture: Facture) {
    const savedFacture = await firstValueFrom(
      this.api.makeAvoirFromFacture(facture),
    );
    this.store.upsertOne(savedFacture);
    return savedFacture;
  }

  @Untrack
  @UpdateStatus
  async sendMail(payload: FactureMailPayload) {
    await firstValueFrom(this.api.sendMail(payload));
  }
}
